// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {EXAMPLE_KONVO_TAG} from '@app/features/app/config/I18nDisplayConstants';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import {
	CANCEL_DESCRIPTOR,
	CONTINUE_DESCRIPTOR,
	USERNAME_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/FormInput';
import {UsernameValidationRules} from '@app/features/ui/components/form/UsernameValidationRules';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import styles from '@app/features/user/components/modals/KonvoTagChangeModal.module.css';
import type {User} from '@app/features/user/models/User';
import {isVisionaryDiscriminator0000Blocked} from '@app/features/user/utils/KonvoTagDiscriminatorUtils';
import {UserPremiumTypes} from '@konvo/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef} from 'react';
import {Controller, useForm} from 'react-hook-form';

const USERNAME_ALREADY_TAKEN_DESCRIPTOR = msg({
	message: 'Username already taken',
	comment: 'Short label in the KonvoTag change modal. Keep it concise.',
});
const USERNAME_UPDATED_DESCRIPTOR = msg({
	message: 'Username updated',
	comment: 'Short label in the KonvoTag change modal. Keep it concise.',
});
const CHANGE_USERNAME_FORM_DESCRIPTOR = msg({
	message: 'Change username form',
	comment: 'Short label in the KonvoTag change modal. Keep it concise.',
});
const CHANGE_YOUR_USERNAME_DESCRIPTOR = msg({
	message: 'Change your username',
	comment: 'Short label in the KonvoTag change modal. Keep it concise.',
});
const TAG_DESCRIPTOR = msg({
	message: 'Tag',
	comment: 'Short label in the KonvoTag change modal. Keep it concise.',
});
const CUSTOM_DISCRIMINATORS_ARE_NOT_AVAILABLE_ON_THIS_INSTANCE_DESCRIPTOR = msg({
	message: 'Custom discriminators are not available on this instance',
	comment: 'Error message in the KonvoTag change modal.',
});
const THE_0000_TAG_IS_RESERVED_FOR_VISIONARY_SUBSCRIBERS_DESCRIPTOR = msg({
	message: 'The #0000 tag is reserved for Visionary subscribers. Visionary is no longer available for purchase.',
	comment: 'Description text in the KonvoTag change modal.',
});

interface FormInputs {
	username: string;
	discriminator: string;
}

interface KonvoTagChangeModalProps {
	user: User;
}

export const KonvoTagChangeModal = observer(({user}: KonvoTagChangeModalProps) => {
	const {i18n} = useLingui();
	const usernameRef = useRef<HTMLInputElement>(null);
	const hasCustomDiscriminator = isLimitToggleEnabled(
		{feature_custom_discriminator: LimitResolver.resolve({key: 'feature_custom_discriminator', fallback: 0})},
		'feature_custom_discriminator',
	);
	const isVisionary = user.premiumType === UserPremiumTypes.LIFETIME;
	const skipAvailabilityCheckRef = useRef(false);
	const resubmitHandlerRef = useRef<(() => Promise<void>) | null>(null);
	const confirmedRerollRef = useRef(false);
	const form = useForm<FormInputs>({
		defaultValues: {
			username: user.username,
			discriminator: user.discriminator,
		},
	});
	useEffect(() => {
		const subscription = form.watch((_, info) => {
			if (info?.name === 'username') {
				confirmedRerollRef.current = false;
			}
		});
		return () => {
			subscription.unsubscribe();
		};
	}, [form]);
	const onSubmit = useCallback(
		async (data: FormInputs) => {
			const usernameValue = data.username.trim();
			const normalizedDiscriminator = data.discriminator;
			const currentUsername = user.username.trim();
			const currentDiscriminator = user.discriminator;
			const isSameTag = usernameValue === currentUsername && normalizedDiscriminator === currentDiscriminator;
			if (!hasCustomDiscriminator && !skipAvailabilityCheckRef.current && !confirmedRerollRef.current) {
				const tagTaken = await UserCommands.checkKonvoTagAvailability({
					username: usernameValue,
					discriminator: normalizedDiscriminator,
				});
				if (tagTaken && !isSameTag) {
					const konvoTag = `${usernameValue}#${normalizedDiscriminator}`;
					ModalCommands.push(
						modal(() => (
							<ConfirmModal
								title={i18n._(USERNAME_ALREADY_TAKEN_DESCRIPTOR)}
								description={
									<div
										className={styles.confirmDescription}
										data-flx="user.konvo-tag-change-modal.on-submit.confirm-description"
									>
										<p data-flx="user.konvo-tag-change-modal.on-submit.p">
											<Trans>
												The username <strong data-flx="user.konvo-tag-change-modal.on-submit.strong">{konvoTag}</strong>{' '}
												is already taken. Continuing will reroll your tag automatically.
											</Trans>
										</p>
										<p
											className={styles.confirmSecondary}
											data-flx="user.konvo-tag-change-modal.on-submit.confirm-secondary"
										>
											<Trans>Cancel if you want to choose a different username instead.</Trans>
										</p>
									</div>
								}
								primaryText={i18n._(CONTINUE_DESCRIPTOR)}
								secondaryText={i18n._(CANCEL_DESCRIPTOR)}
								primaryVariant="primary"
								onPrimary={async () => {
									confirmedRerollRef.current = true;
									skipAvailabilityCheckRef.current = true;
									try {
										await resubmitHandlerRef.current?.();
									} finally {
										skipAvailabilityCheckRef.current = false;
									}
								}}
								data-flx="user.konvo-tag-change-modal.on-submit.confirm-modal"
							/>
						)),
					);
					return;
				}
			}
			await UserCommands.update({
				username: usernameValue,
				discriminator: normalizedDiscriminator,
			});
			if (skipAvailabilityCheckRef.current) {
				skipAvailabilityCheckRef.current = false;
			}
			ModalCommands.pop();
			ToastCommands.createToast({type: 'success', children: i18n._(USERNAME_UPDATED_DESCRIPTOR)});
		},
		[hasCustomDiscriminator],
	);
	const {handleSubmit, isSubmitting} = useFormSubmit({
		form,
		onSubmit,
		defaultErrorField: 'username',
	});
	resubmitHandlerRef.current = handleSubmit;
	return (
		<Modal.Root size="small" centered initialFocusRef={usernameRef} data-flx="user.konvo-tag-change-modal.modal-root">
			<Form
				form={form}
				onSubmit={handleSubmit}
				aria-label={i18n._(CHANGE_USERNAME_FORM_DESCRIPTOR)}
				data-flx="user.konvo-tag-change-modal.form.submit"
			>
				<Modal.Header
					title={i18n._(CHANGE_YOUR_USERNAME_DESCRIPTOR)}
					data-flx="user.konvo-tag-change-modal.modal-header"
				/>
				<Modal.Content data-flx="user.konvo-tag-change-modal.modal-content">
					<Modal.ContentLayout data-flx="user.konvo-tag-change-modal.modal-content-layout">
						<Modal.Description data-flx="user.konvo-tag-change-modal.modal-description">
							<Trans>
								Usernames can only contain letters (a-z, A-Z), numbers (0-9), and underscores. Usernames are
								case-insensitive.
							</Trans>
						</Modal.Description>
						<div className={styles.konvoTagContainer} data-flx="user.konvo-tag-change-modal.konvo-tag-container">
							<span className={styles.konvoTagLabel} data-flx="user.konvo-tag-change-modal.konvo-tag-label">
								<Trans>Username</Trans>
							</span>
							{(form.formState.errors.username || form.formState.errors.discriminator) && (
								<div className={styles.errorBox} role="alert" data-flx="user.konvo-tag-change-modal.error-box">
									{form.formState.errors.username?.message || form.formState.errors.discriminator?.message}
								</div>
							)}
							<div className={styles.konvoTagInputRow} data-flx="user.konvo-tag-change-modal.konvo-tag-input-row">
								<div className={styles.usernameInput} data-flx="user.konvo-tag-change-modal.username-input">
									<Controller
										name="username"
										control={form.control}
										render={({field}) => (
											<Input
												data-flx="user.konvo-tag-change-modal.input.text"
												{...field}
												ref={usernameRef}
												autoComplete="username"
												aria-label={i18n._(USERNAME_DESCRIPTOR)}
												placeholder={EXAMPLE_KONVO_TAG}
												required={true}
												type="text"
											/>
										)}
										data-flx="user.konvo-tag-change-modal.controller"
									/>
								</div>
								<span className={styles.separator} data-flx="user.konvo-tag-change-modal.separator">
									#
								</span>
								<div className={styles.discriminatorInput} data-flx="user.konvo-tag-change-modal.discriminator-input">
									{!hasCustomDiscriminator ? (
										<Tooltip
											text={i18n._(CUSTOM_DISCRIMINATORS_ARE_NOT_AVAILABLE_ON_THIS_INSTANCE_DESCRIPTOR)}
											data-flx="user.konvo-tag-change-modal.tooltip--2"
										>
											<div
												className={styles.discriminatorInputDisabled}
												data-flx="user.konvo-tag-change-modal.discriminator-input-disabled--2"
											>
												<Input
													data-flx="user.konvo-tag-change-modal.input.set-value.text--2"
													{...form.register('discriminator')}
													aria-label={i18n._(TAG_DESCRIPTOR)}
													maxLength={4}
													placeholder="0000"
													required={true}
													type="text"
													disabled={true}
													onChange={(e) => {
														const value = e.target.value.replace(/\D/g, '');
														form.setValue('discriminator', value);
													}}
												/>
											</div>
										</Tooltip>
									) : (
										<Input
											data-flx="user.konvo-tag-change-modal.input.set-value.text--3"
											{...form.register('discriminator', {
												validate: (value) =>
													isVisionaryDiscriminator0000Blocked({
														showPremium: false,
														isVisionary,
														discriminator: value,
													})
														? i18n._(THE_0000_TAG_IS_RESERVED_FOR_VISIONARY_SUBSCRIBERS_DESCRIPTOR)
														: true,
											})}
											aria-label={i18n._(TAG_DESCRIPTOR)}
											maxLength={4}
											placeholder="0000"
											required={true}
											type="text"
											disabled={false}
											onChange={(e) => {
												const value = e.target.value.replace(/\D/g, '');
												form.setValue('discriminator', value, {shouldValidate: true});
											}}
										/>
									)}
								</div>
							</div>
							<div className={styles.validationBox} data-flx="user.konvo-tag-change-modal.validation-box">
								<UsernameValidationRules
									username={form.watch('username')}
									data-flx="user.konvo-tag-change-modal.username-validation-rules"
								/>
							</div>
						</div>
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer data-flx="user.konvo-tag-change-modal.modal-footer">
					<Button onClick={ModalCommands.pop} variant="secondary" data-flx="user.konvo-tag-change-modal.button.pop">
						<Trans>Cancel</Trans>
					</Button>
					<Button type="submit" submitting={isSubmitting} data-flx="user.konvo-tag-change-modal.button.submit">
						<Trans>Continue</Trans>
					</Button>
				</Modal.Footer>
			</Form>
		</Modal.Root>
	);
});
